import {GitBranchIcon, TagIcon, TriangleDownIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Button, type AnchoredOverlayProps} from '@primer/react'
import {useCallback, useId, useMemo} from 'react'

import type {RefSelectorProps, RefType} from './RefSelector'
import {FocusKeys} from '@primer/behaviors'

import styles from './RefSelectorAnchoredOverlay.module.css'
import {clsx} from 'clsx'

type PickedRefSelectorProps = Pick<
  RefSelectorProps,
  'hotKey' | 'onOpenChange' | 'size' | 'variant' | 'anchoredOverlayProps'
>

export interface RefSelectorAnchoredOverlayProps extends PickedRefSelectorProps {
  ariaDescribedBy?: string
  ariaLabelledBy?: string
  ariaLabel?: string
  overlayOpen: boolean
  onOverlayChange: (open: boolean) => void
  preventClosing?: boolean
  focusTrapEnabled?: boolean
  refType: RefType
  inputRef?: React.RefObject<HTMLInputElement | null>
  buttonClassName?: string
  buttonText?: string
  persistButtonText?: boolean
  buttonPrefix?: string
  showIcon?: boolean
  allowResizing?: boolean
  idEnding?: string
  useFocusZone?: boolean
  displayCommitish: string
  disabled?: boolean
  renderAnchor?: AnchoredOverlayProps['renderAnchor']
}

export function RefSelectorAnchoredOverlay(props: React.PropsWithChildren<RefSelectorAnchoredOverlayProps>) {
  const {
    ariaDescribedBy,
    ariaLabelledBy,
    ariaLabel,
    hotKey,
    onOpenChange,
    size,
    displayCommitish,
    variant,
    refType,
    children,
    preventClosing,
    inputRef,
    overlayOpen,
    onOverlayChange,
    focusTrapEnabled = true,
    buttonClassName,
    buttonText,
    persistButtonText = true,
    buttonPrefix,
    showIcon = true,
    allowResizing,
    useFocusZone,
    disabled,
    anchoredOverlayProps,
    renderAnchor,
  } = props

  // If we render multiple ref pickers on the page we need to ensure unique ids.
  const baseExtraIdEnding = useId()

  const extraIdEnding = `-${props.idEnding || baseExtraIdEnding}`
  const buttonId = `ref-picker${extraIdEnding}`

  const textAreaId = 'read-only-cursor-text-area'

  const onOverlayOpenChange = useCallback(
    (open: boolean) => {
      onOverlayChange(open)
      onOpenChange?.(open)
    },
    [onOpenChange, onOverlayChange],
  )

  const focusTrapSettings = useMemo(() => {
    if (focusTrapEnabled) {
      // Setting disabled to false does not work. Therefore, I need to return different object
      return {initialFocusRef: inputRef}
    }

    return {initialFocusRef: inputRef, disabled: true}
  }, [focusTrapEnabled, inputRef])

  const getButtonText = () => {
    // If buttonPrefix is provided, we will always prepend it to the button text.
    // If buttonText is provided and persistButtonText is not set to false (it is true by default), use buttonText.
    // If buttonText is provided, and persistButtonText is false, use non-empty displayCommitish. If it is empty, use buttonText.
    // If buttonText is not provided, use displayCommitish as is.
    const nonEmptyDisplayCommitish = displayCommitish.length > 0
    const text =
      buttonText && persistButtonText ? buttonText : nonEmptyDisplayCommitish ? displayCommitish : buttonText || ''

    return buttonPrefix ? (
      <>
        <span className="fgColor-muted">{buttonPrefix}:</span>
        <span> {text}</span>
      </>
    ) : (
      text
    )
  }

  return (
    <AnchoredOverlay
      open={overlayOpen}
      overlayProps={{
        role: 'dialog',
        width: 'medium',
        'aria-label': refType === 'branch' ? 'Select a branch' : 'Select a tag',
      }}
      onOpen={() => onOverlayOpenChange(true)}
      // Prevent closing overlay, while working with the dialog
      onClose={() => !preventClosing && onOverlayOpenChange(false)}
      renderAnchor={anchorProps => {
        if (renderAnchor) {
          return renderAnchor({
            ...anchorProps,
            'aria-labelledby': ariaLabelledBy,
            disabled,
          })
        }
        return (
          <>
            <Button
              {...anchorProps}
              data-hotkey={hotKey}
              disabled={disabled}
              size={size}
              variant={variant}
              style={{
                minWidth: allowResizing ? 0 : undefined,
              }}
              trailingVisual={TriangleDownIcon}
              aria-describedby={ariaDescribedBy}
              aria-labelledby={ariaLabelledBy}
              aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? `${displayCommitish} ${refType}`)}
              data-testid="anchor-button"
              id={buttonId}
              className={clsx(buttonClassName, styles.RefSelectorOverlayBtn)}
            >
              <div className={styles.RefSelectorOverlayContainer}>
                {showIcon && (
                  <div className={styles.RefSelectorOverlayHeader}>
                    {refType === 'tag' ? <TagIcon size="small" /> : <GitBranchIcon size="small" />}
                  </div>
                )}
                <div
                  style={{
                    maxWidth: allowResizing ? undefined : 125,
                  }}
                  className={clsx('ref-selector-button-text-container', styles.RefSelectorBtnTextContainer)}
                >
                  <span className={styles.RefSelectorText}>&nbsp;{getButtonText()}</span>
                </div>
              </div>
            </Button>
            {/* this button is necessary so that the ref selector hotkey still works while the blob text area has focus
  and it doesnt' do anything if the text area does not have focus*/}
            <button
              hidden
              data-testid="ref-selector-hotkey-button"
              data-hotkey={hotKey}
              onClick={() => onOverlayOpenChange(true)}
              data-hotkey-scope={textAreaId}
              disabled={disabled}
            />
          </>
        )
      }}
      focusTrapSettings={focusTrapSettings}
      focusZoneSettings={useFocusZone ? {bindKeys: FocusKeys.ArrowAll | FocusKeys.Tab} : {disabled: true}}
      {...anchoredOverlayProps}
    >
      {/* eslint-disable-next-line github/a11y-role-supports-aria-props */}
      <div data-testid="overlay-content" aria-labelledby={buttonId} id="selectPanel" style={{height: '100%'}}>
        {children}
      </div>
    </AnchoredOverlay>
  )
}

try{ RefSelectorAnchoredOverlay.displayName ||= 'RefSelectorAnchoredOverlay' } catch {}